import { useGetPermanentEnsemblesRelatedToArtwork } from "@/api/artwork/artwork";
import { useGetAllAttachments } from "@/api/attachment";
import { useUploadDocument } from "@/api/file";
import { useGetPermanentEnsembles } from "@/api/permanent-ensemble";
import {
  administrativeDocumentTypeSchema,
  templateStyleSchema,
  useGenerateAdministrativeDocumentsPdf,
  useGetCertificateLogo,
  type ExportFormat,
} from "@/api/pdf";
import * as AF from "@/components/form-items/AbstractForm";
import PDFViewer from "@/components/print/PDFViewer";
import SelectedRecord from "@/components/print/SelectedRecord";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CancelButton } from "@/components/ui/custom-buttons";
import { CustomDialogFooter } from "@/components/ui/custom-dialog";
import {
  CustomSheetBody,
  CustomSheetContent,
  CustomSheetHeader,
} from "@/components/ui/custom-sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  HeadlessFormItem,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { PreviewDocumentUI } from "@/components/ui/preview-document-ui.tsx";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetClose,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabsContained";
import { useAuth } from "@/context/auth/AuthContext";
import i18n, { getMainLanguage, SupportedLocale } from "@/i18n";
import { isSculptureModuleEnabled } from "@/lib/compute-field/artwork-module-helpers";
import {
  ARTWORK_DOCUMENT_MAIN_BACK_POSITIONS,
  ARTWORK_DOCUMENT_PRIMARY_POSITION_EXCLUSIONS,
  getDefaultImage,
} from "@/lib/image";
import { Artwork, Attachment } from "@/lib/types";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, FileDown, FileText, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = z.object({
  documentType: administrativeDocumentTypeSchema,
  templateStyle: templateStyleSchema.default("standard"),
  showLogo: z.boolean().default(true),

  inclusionLetter: z.boolean().default(false),
  informationLetter: z.boolean().default(false),
  hasBeenExamined: z.boolean().default(false),

  signingInfo: z.string().min(1),
  documentNumber: z.string().optional(),
  additionalParagraph: z.string().optional(),

  showInscriptionsDos: z.boolean().default(false),
  showImageDos: z.boolean().default(false),
  hideDate: z.boolean().default(false),
  selectedAttachmentIds: z.array(z.string()).default([]),
});

type FormValues = z.infer<typeof formSchema>;

const STEPS = ["printOptions", "templateChoice", "preview"] as const;
type Step = (typeof STEPS)[number];

const TemplateCard = ({
  selected,
  onClick,
  label,
  variant,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
  variant: "standard" | "centered";
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex flex-col gap-3 items-center text-left cursor-pointer transition-all",
      "flex-1",
    )}
  >
    {/* Checkbox + label */}
    <span className="flex items-center gap-2 self-start">
      <span
        className={cn(
          "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border transition-colors",
          selected
            ? "border-primary bg-primary text-primary-foreground"
            : "border-slate-300 bg-white",
        )}
      >
        {selected && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            className="text-white"
          >
            <path
              d="M10 3L4.5 8.5L2 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      <span className="text-sm font-medium">{label}</span>
    </span>

    {/* Wireframe preview */}
    <div
      className={cn(
        "w-full aspect-[3/4] rounded-lg border-2 p-4 flex flex-col gap-2 transition-colors",
        selected ? "border-primary bg-blue-50/40" : "border-slate-200 bg-white",
      )}
    >
      <>
        <PreviewDocumentUI variant={variant} />
      </>
    </div>
  </button>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const AddAdministrativeDocumentsDrawer = ({
  artworkAttachments,
  artwork,
  disabled = false,
}: {
  artwork: Artwork;
  artworkAttachments: Attachment[];
  disabled?: boolean;
}) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      documentType: "document1",
      templateStyle: "standard",
      showLogo: true,
      documentNumber: "",
      additionalParagraph: "",
      showInscriptionsDos: false,
      showImageDos: false,
      hideDate: false,
      selectedAttachmentIds: [],
    },
  });

  const { t } = useTranslation();

  // Certificate logo state
  const { data: certificateLogoData } = useGetCertificateLogo();
  const hasLogo = Boolean(certificateLogoData?.logoUrl);

  useEffect(() => {
    if (!hasLogo) {
      form.setValue("showLogo", false);
    }
  }, [hasLogo, form]);

  const { data: permanentEnsembles } = useGetPermanentEnsemblesRelatedToArtwork(
    artwork.id,
  );

  const queryClient = useQueryClient();

  // Une œuvre hors CR peut aussi produire un document (#1093) : on prend
  // alors l'ensemble fixe de l'instance (Réglages du CR), comme l'API.
  const { data: instanceCrEnsembles } = useGetPermanentEnsembles();

  const selectedPermanentEnsemble = useMemo(
    () => permanentEnsembles?.at(0) ?? instanceCrEnsembles?.at(0),
    [permanentEnsembles, instanceCrEnsembles],
  );

  const pdfGenerator = useGenerateAdministrativeDocumentsPdf();
  const fileSaver = useUploadDocument();
  const { instanceData } = useAuth();

  const artworkAttachmentProfile = useMemo(() => {
    const locale = getMainLanguage();
    if (
      instanceData &&
      isSculptureModuleEnabled(artwork, instanceData, locale)
    ) {
      return "sculptureEdition" as const;
    }
    return "artwork" as const;
  }, [artwork, instanceData]);

  const createdFile = useRef<File>();
  const [previewURL, setPreviewURL] = useState<string | null>(null);

  const [activeStep, setActiveStep] = useState<Step>("printOptions");
  const [sheetOpen, setSheetOpen] = useState(false);

  const image = useMemo(
    () =>
      getDefaultImage(
        artworkAttachments,
        artworkAttachmentProfile,
        "publicUrl",
      ),
    [artworkAttachments, artworkAttachmentProfile],
  );

  // Back image detection
  const backImage = useMemo(
    () =>
      artworkAttachments.find((a) =>
        (ARTWORK_DOCUMENT_MAIN_BACK_POSITIONS as readonly string[]).includes(
          a.position,
        ),
      ),
    [artworkAttachments],
  );

  // Secondary attachments (exclude face and back)
  const { data: secondaryAttachments = [] } = useGetAllAttachments({
    where: {
      docId: artwork.id,
      position: {
        notIn: [...ARTWORK_DOCUMENT_PRIMARY_POSITION_EXCLUSIONS],
      },
    },
    order: { order: "asc" },
  });

  const watchShowImageDos = form.watch("showImageDos");
  const watchSelectedAttachmentIds = form.watch("selectedAttachmentIds");
  const isMaxSecondaryReached = watchSelectedAttachmentIds.length >= 2;

  useEffect(() => {
    if (!sheetOpen) {
      setActiveStep("printOptions");
      setPreviewURL(null);
      createdFile.current = undefined;
      form.reset();
    }
  }, [sheetOpen, form]);

  const generatePdf = useCallback(
    (values: FormValues) => {
      pdfGenerator.mutate(
        {
          artworkId: artwork.id,
          params: {
            documentType: values.documentType,
            templateStyle: values.templateStyle,
            oeuvreExaminee: values.hasBeenExamined,
            signloca: values.signingInfo,
            showLogo: values.showLogo && hasLogo,
            documentNumber: values.documentNumber ?? "",
            additionalParagraph: values.additionalParagraph ?? "",
            showInscriptionsDos: values.showInscriptionsDos,
            showImageDos: values.showImageDos,
            hideDate: values.hideDate,
            selectedAttachmentIds: values.selectedAttachmentIds,
          },
          lang: i18n.language as SupportedLocale,
        },
        {
          onSuccess: (file) => {
            createdFile.current = file;
            const url = URL.createObjectURL(file);
            setPreviewURL(url);
            setActiveStep("preview");
          },
        },
      );
    },
    [artwork.id, hasLogo, pdfGenerator],
  );

  const handleNext = useCallback(() => {
    if (activeStep === "printOptions") {
      setActiveStep("templateChoice");
    } else if (activeStep === "templateChoice") {
      const values = form.getValues();
      generatePdf(values);
    }
  }, [activeStep, form, generatePdf]);

  const handleSave = useCallback(
    (format: ExportFormat) => {
      if (!instanceData) return;

      const values = form.getValues();
      const fileName = `${artwork.oeuvresDescriptionNumeroEnregistrement}_Document Administratif`;
      const ext = format === "word" ? "docx" : "pdf";
      const mimetype =
        format === "word"
          ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          : "application/pdf";

      const saveAndDownload = (file: File) => {
        fileSaver.mutate(
          {
            file,
            data: {
              docId: artwork.id,
              mimetype,
              sourceName: `${fileName}.${ext}`,
              title: `${fileName}.${ext}`,
              tag: "adminRecord",
              description: "",
            },
            s3bucket: instanceData.s3Bucket,
            instanceId: instanceData.id,
          },
          {
            onSuccess: () => {
              try {
                const url = URL.createObjectURL(file);
                const link = document.createElement("a");
                link.href = url;
                link.download = `${fileName}.${ext}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
              } catch (error) {
                console.error(error);
              }

              setSheetOpen(false);
              queryClient.invalidateQueries({
                queryKey: ["artworks", artwork.id, "administrative-documents"],
              });
              form.reset();
            },
          },
        );
      };

      // For PDF: reuse the preview file already generated (no new API call)
      // For Word: must generate via API (different format)
      if (format === "pdf" && createdFile.current) {
        saveAndDownload(createdFile.current);
      } else {
        pdfGenerator.mutate(
          {
            artworkId: artwork.id,
            params: {
              documentType: values.documentType,
              templateStyle: values.templateStyle,
              oeuvreExaminee: values.hasBeenExamined,
              signloca: values.signingInfo,
              showLogo: values.showLogo && hasLogo,
              documentNumber: values.documentNumber ?? "",
              additionalParagraph: values.additionalParagraph ?? "",
              showInscriptionsDos: values.showInscriptionsDos,
              showImageDos: values.showImageDos,
              hideDate: values.hideDate,
              selectedAttachmentIds: values.selectedAttachmentIds,
            },
            lang: i18n.language as SupportedLocale,
            format,
          },
          {
            onSuccess: (file) => saveAndDownload(file),
          },
        );
      }
    },
    [
      artwork,
      instanceData,
      hasLogo,
      pdfGenerator,
      fileSaver,
      queryClient,
      form,
    ],
  );

  const activeStepIndex = STEPS.indexOf(activeStep);

  const isStep1Valid = form.formState.isValid;

  const getFooterButton = () => {
    switch (activeStep) {
      case "printOptions":
        return {
          label: t("next"),
          onClick: handleNext,
          disabled: !isStep1Valid,
          loading: false,
        };
      case "templateChoice":
        return {
          label: t("administrativeDocumentsPrevisualiserBouton"),
          onClick: handleNext,
          disabled: pdfGenerator.isPending,
          loading: pdfGenerator.isPending,
        };
      case "preview":
        return {
          label: "",
          onClick: () => {},
          disabled: false,
          loading: false,
          isDropdown: true,
        };
    }
  };

  const footer = getFooterButton();

  return (
    <Sheet onOpenChange={setSheetOpen} open={sheetOpen}>
      <SheetTrigger asChild>
        <Button variant="outline">
          {t("oeuvresCreateNewAdministrativeDocument")}
        </Button>
      </SheetTrigger>
      <CustomSheetContent className="w-[850px] sm:max-w-[850px]">
        <Form {...form}>
          <form
            onSubmit={(e) => e.preventDefault()}
            className="flex flex-col h-full"
          >
            <CustomSheetHeader>
              <SheetTitle>{t("administrativeDocumentCreerTitle")}</SheetTitle>
              <p className="text-sm text-muted-foreground">
                {t("administrativeDocumentDescription")}
              </p>
            </CustomSheetHeader>

            <CustomSheetBody className="flex-1 overflow-y-auto">
              {/* Selected artwork */}
              <div className="flex flex-col items-center gap-3 mb-6">
                <span className="font-semibold font-[Raleway] text-base">
                  {t("artworksIncluded")}
                </span>
                <SelectedRecord
                  image={image}
                  title={[
                    artwork.oeuvresDescriptionTitre,
                    artwork.oeuvresDescriptionNumeroEnregistrement,
                  ]
                    .filter(Boolean)
                    .join(" - ")}
                />
              </div>

              <Separator className="mb-6" />

              {/* Stepper tabs */}
              <Tabs
                className="w-full flex flex-col items-center"
                value={activeStep}
                onValueChange={(v) => {
                  const step = v as Step;
                  const targetIndex = STEPS.indexOf(step);
                  if (targetIndex <= activeStepIndex) {
                    setActiveStep(step);
                  }
                }}
              >
                <TabsList className="h-9 mb-6">
                  {STEPS.map((step, index) => {
                    const isActive = activeStep === step;
                    const stepLabels: Record<Step, string> = {
                      printOptions: t("adminDocStepPrintOptions"),
                      templateChoice: t("adminDocStepTemplateChoice"),
                      preview: t("adminDocStepPreview"),
                    };

                    return (
                      <TabsTrigger
                        key={step}
                        value={step}
                        disabled={index > activeStepIndex}
                        className={cn(
                          "flex items-center gap-1.5 text-[11px] px-2.5 py-1.5",
                          !isActive && "bg-slate-100",
                        )}
                      >
                        <Badge
                          variant={isActive ? "outlinedActive" : "outlined"}
                          className="h-4 min-w-4 text-[10px] px-1 flex items-center justify-center leading-none"
                        >
                          {index + 1}
                        </Badge>
                        {stepLabels[step]}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {/* ---- Step 1: Print options ---- */}
                <TabsContent value="printOptions" className="w-full">
                  <AF.Col className="gap-4">
                    <Label className="text-lg">{t("printOptions")}</Label>

                    <AF.Col className="gap-2">
                      <FormLabel>{t("documentType")}</FormLabel>
                      <FormField
                        control={form.control}
                        name="documentType"
                        render={({ field }) => (
                          <HeadlessFormItem>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                                className="flex flex-col"
                              >
                                <FormItem className="flex items-center space-x-3 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="document1" />
                                  </FormControl>
                                  <FormLabel className="font-normal">
                                    {selectedPermanentEnsemble?.ensemblesTitreDocument1 ||
                                      t("bo.print.mp.document1")}
                                  </FormLabel>
                                </FormItem>
                                <FormItem className="flex items-center space-x-3 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="document2" />
                                  </FormControl>
                                  <FormLabel className="font-normal">
                                    {selectedPermanentEnsemble?.ensemblesTitreDocument2 ||
                                      t("bo.print.mp.document2")}
                                  </FormLabel>
                                </FormItem>
                                {selectedPermanentEnsemble?.ensemblesTitreDocument3 && (
                                  <FormItem className="flex items-center space-x-3 space-y-0">
                                    <FormControl>
                                      <RadioGroupItem value="document3" />
                                    </FormControl>
                                    <FormLabel className="font-normal">
                                      {
                                        selectedPermanentEnsemble.ensemblesTitreDocument3
                                      }
                                    </FormLabel>
                                  </FormItem>
                                )}
                              </RadioGroup>
                            </FormControl>
                          </HeadlessFormItem>
                        )}
                      />
                      <AF.Checkbox
                        name="hasBeenExamined"
                        label={t("oeuvreExaminee")}
                      />
                    </AF.Col>

                    <div className="max-w-[400px]">
                      <AF.Text name="signingInfo" label={t("signloca")} />
                    </div>
                    <div className="max-w-[400px]">
                      <AF.Text
                        name="documentNumber"
                        label={t("documentNumber")}
                      />
                    </div>
                    <AF.RichText
                      name="additionalParagraph"
                      label={t("additionalParagraph")}
                    />

                    <Separator />

                    {/* Masquer la date d'edition en pied de page */}
                    <FormField
                      control={form.control}
                      name="hideDate"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="font-normal">
                            {t("hideDate")}
                          </FormLabel>
                        </FormItem>
                      )}
                    />

                    {/* Inscription au dos */}
                    <FormField
                      control={form.control}
                      name="showInscriptionsDos"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="font-normal">
                            {t("showInscriptionsDos")}
                          </FormLabel>
                        </FormItem>
                      )}
                    />

                    {/* Afficher l'image au dos */}
                    <FormField
                      control={form.control}
                      name="showImageDos"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={(checked) => {
                                field.onChange(checked);
                                if (checked) {
                                  form.setValue("selectedAttachmentIds", []);
                                }
                              }}
                              disabled={!backImage}
                            />
                          </FormControl>
                          <FormLabel
                            className={cn(
                              "font-normal",
                              !backImage && "text-muted-foreground",
                            )}
                          >
                            {t("showImageDos")}
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                    {!backImage && (
                      <p className="text-sm text-muted-foreground ml-7">
                        {t("noBackImageAvailable")}
                      </p>
                    )}

                    {/* Images secondaires */}
                    {secondaryAttachments.length > 0 && (
                      <div
                        className={cn(
                          "flex flex-col gap-3",
                          watchShowImageDos && "opacity-50 pointer-events-none",
                        )}
                      >
                        <Label className="text-sm">
                          {t("selectSecondaryImages")} (
                          {watchSelectedAttachmentIds.length}/2)
                        </Label>
                        <div className="grid grid-cols-6 gap-3">
                          {secondaryAttachments.map((attachment) => {
                            const isSelected =
                              watchSelectedAttachmentIds.includes(
                                attachment.id,
                              );
                            const isDisabled =
                              !isSelected && isMaxSecondaryReached;

                            return (
                              <div
                                key={attachment.id}
                                className={cn(
                                  "relative aspect-square rounded-md overflow-hidden border-2 transition-all",
                                  isSelected
                                    ? "border-primary"
                                    : "border-slate-200",
                                  isDisabled
                                    ? "opacity-40 cursor-not-allowed"
                                    : "cursor-pointer hover:border-primary",
                                )}
                                onClick={() => {
                                  if (isDisabled) return;
                                  const current = form.getValues(
                                    "selectedAttachmentIds",
                                  );
                                  if (isSelected) {
                                    form.setValue(
                                      "selectedAttachmentIds",
                                      current.filter(
                                        (id) => id !== attachment.id,
                                      ),
                                    );
                                  } else {
                                    form.setValue("selectedAttachmentIds", [
                                      ...current,
                                      attachment.id,
                                    ]);
                                  }
                                }}
                              >
                                <img
                                  src={
                                    attachment.mediumPublicUrl ||
                                    attachment.smallPublicUrl ||
                                    ""
                                  }
                                  alt={
                                    attachment.textAlt?.[
                                      i18n.language as SupportedLocale
                                    ] || ""
                                  }
                                  className="w-full h-full object-cover"
                                />
                                <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] text-center py-0.5 truncate px-1">
                                  {t(attachment.position)}
                                </span>
                                <div
                                  className="absolute top-2 right-2"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    disabled={isDisabled}
                                    onCheckedChange={(checked) => {
                                      const current = form.getValues(
                                        "selectedAttachmentIds",
                                      );
                                      const alreadySelected = current.includes(
                                        attachment.id,
                                      );
                                      if (checked && !alreadySelected) {
                                        form.setValue("selectedAttachmentIds", [
                                          ...current,
                                          attachment.id,
                                        ]);
                                      } else if (
                                        checked === false &&
                                        alreadySelected
                                      ) {
                                        form.setValue(
                                          "selectedAttachmentIds",
                                          current.filter(
                                            (id) => id !== attachment.id,
                                          ),
                                        );
                                      }
                                    }}
                                    className="bg-white"
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </AF.Col>
                </TabsContent>

                {/* ---- Step 2: Template choice ---- */}
                <TabsContent value="templateChoice" className="w-full">
                  <AF.Col className="gap-6">
                    {/* Template style cards */}
                    <FormField
                      control={form.control}
                      name="templateStyle"
                      render={({ field }) => (
                        <HeadlessFormItem>
                          <div className="flex gap-6">
                            <TemplateCard
                              variant="standard"
                              label={t("certificateTemplateStandard")}
                              selected={field.value === "standard"}
                              onClick={() => field.onChange("standard")}
                            />
                            <TemplateCard
                              variant="centered"
                              label={t("certificateTemplateCentered")}
                              selected={field.value === "centered"}
                              onClick={() => field.onChange("centered")}
                            />
                          </div>
                        </HeadlessFormItem>
                      )}
                    />

                    <Separator />

                    {/* Show logo toggle */}
                    <AF.Col className="gap-2">
                      <FormField
                        control={form.control}
                        name="showLogo"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={!hasLogo}
                              />
                            </FormControl>
                            <FormLabel
                              className={cn(
                                "font-normal",
                                !hasLogo && "text-muted-foreground",
                              )}
                            >
                              {t("certificateShowLogo")}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                      {!hasLogo && (
                        <p className="text-sm text-muted-foreground ml-7">
                          {t("certificateNoLogoInfo")}
                        </p>
                      )}
                    </AF.Col>
                  </AF.Col>
                </TabsContent>

                {/* ---- Step 3: Preview ---- */}
                <TabsContent value="preview" className="w-full">
                  <div className="flex flex-col items-center gap-4">
                    {previewURL ? (
                      <div className="max-w-[650px] mx-auto max-h-[500px] overflow-y-auto border rounded-lg p-4 bg-slate-50">
                        <PDFViewer fileURL={previewURL} />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CustomSheetBody>

            {/* Dynamic footer */}
            <CustomDialogFooter>
              <SheetClose asChild>
                <CancelButton className="flex-1" />
              </SheetClose>
              {activeStep === "preview" ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      className="flex-1"
                      type="button"
                      variant="default"
                      disabled={
                        fileSaver.isPending ||
                        pdfGenerator.isPending ||
                        disabled
                      }
                    >
                      {(fileSaver.isPending || pdfGenerator.isPending) && (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      )}
                      {t("saveDocument")}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleSave("pdf")}>
                      <FileDown className="w-4 h-4 mr-2" />
                      {t("saveAsPdf")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleSave("word")}>
                      <FileText className="w-4 h-4 mr-2" />
                      {t("saveAsWord")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  className="flex-1"
                  type="button"
                  variant="default"
                  disabled={footer.disabled}
                  onClick={footer.onClick}
                >
                  {footer.loading && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  {footer.label}
                </Button>
              )}
            </CustomDialogFooter>
          </form>
        </Form>
      </CustomSheetContent>
    </Sheet>
  );
};
